import { platformAction } from '~/helpers/store'
import { splitStringBySeparator } from '~/helpers'
import { defaultState, SEARCHES } from '../constants'

export default {
  async fetchNote ({ commit }, noteId) {
    await platformAction({
      client: this.$api.content.houseNotes,
      method: 'get',
      params: noteId,
      commit,
      logError: this.$logError,
      include: ['sections']
    })
  },

  async fetchNotes ({ commit, getters }, { params = {}, more = false }) {
    const withNoteFilters = getters.applyNoteFilters({ params })
    const cachedSearch = getters.cachedSearch(withNoteFilters)
    let minPage = 1

    let maxPage = cachedSearch ? cachedSearch.pages.length : 1

    if (cachedSearch && more) {
      minPage = cachedSearch.pages.length + 1
      maxPage = minPage
      if (minPage > cachedSearch.totalPages) {
        return
      }
    }


    let page = minPage
    const searches = []

    const commitWrapper = (mutation, mutationParams) => {
      switch (mutation) {
        case 'fetching':
          commit('searchFetching', withNoteFilters)
          break
        case 'fetched':
          commit('searchFetched', withNoteFilters)
          break
        case 'failed':
          commit('searchFailed', { params: withNoteFilters, failed: mutationParams })
          break

        default:
          commit(mutation, mutationParams)
      }
    }

    while (page <= maxPage) {
      const withPage = Object.assign({}, withNoteFilters, { page })

      searches.push(platformAction({
        client: this.$api.content.houseNotes,
        params: withPage,
        commit: commitWrapper,
        logError: this.$logError,
        simple: false,
        include: []
      }))

      page += 1
    }

    const results = await Promise.all(searches)
    results.forEach(res => {
      if (!res) return
      const { data: notes, meta } = res
      if (notes) {
        commit('saveSearch', { params: withNoteFilters, notes, meta })
      }
    })
  },

  setNoteFilter ({ commit }, { filter, value }) {
    if (!defaultState.filters[filter]) {
      return
    }

    const defaultValue = defaultState.filters[filter]

    if (defaultValue !== undefined) {
      commit('setNoteFilter', { filter, value })
    }
  },

  async setNoteFilters ({ dispatch }, { filters }) {
    Object.entries(filters).forEach(([filter, value]) => dispatch('setNoteFilter', { filter, value }))
  },

  async rehydrateNoteFilters ({ commit }, { query }) {
    const filters = Object.entries(query).reduce((acc, [key, val]) => ({
      ...acc,
      [key]: Array.isArray(defaultState.filters[key]) ? splitStringBySeparator(val) : val
    }), defaultState.filters)

    commit('rehydrateNoteFilters', { filters })
  },

  unsetNoteFilter ({ commit }, { filter }) {
    if (!defaultState.filters[filter]) {
      return
    }
    const defaultValue = defaultState.filters[filter]
    if (defaultValue !== undefined) {
      commit('unsetNoteFilter', { filter, value: defaultValue })
    }
  },

  unsetNoteFilters ({ dispatch }, { filters }) {
    filters.forEach(filter => dispatch('unsetNoteFilter', { filter }))
  },

  resetNoteFilters ({ commit, getters }) {
    const defaultFilters = getters.defaultFilters
    commit('resetNoteFilters', defaultFilters)
  },

  fetchAllNotes ({ dispatch }, more) {
    return dispatch('fetchNotes', { params: SEARCHES.ALL, more })
  },

  fetchFeaturedNotes ({ dispatch }) {
    return dispatch('fetchNotes', { params: SEARCHES.FEATURED })
  },

  fetchLatestNotes ({ dispatch }) {
    return dispatch('fetchNotes', { params: SEARCHES.LATEST })
  },

  async fetchPreferencedNotes ({ dispatch, rootGetters }) {
    const params = Object.assign({}, SEARCHES.PREFERENCED)
    const favoriteContentCategories = rootGetters['user/account/favoriteContentCategories']
    if (favoriteContentCategories.length > 0) {
      params.filter.contentCategoryId = favoriteContentCategories.map(cat => cat.id).toString()
      dispatch('fetchNotes', { params })
    }
  },

  async fetchSubscribedNotes ({ dispatch, rootGetters }) {
    const params = Object.assign({}, SEARCHES.SUBSCRIBED)
    const favoriteContentCategories = rootGetters['user/account/favoriteContentCategories']
    if (favoriteContentCategories.length > 0) {
      params.filter.contentCategoryId = favoriteContentCategories.map(cat => cat.id).toString()
      dispatch('fetchNotes', { params })
    }
  },

  async fetchVenueNotes ({ dispatch }, id) {
    const params = { ...SEARCHES.VENUE }
    params.filter.venue_id = id
    dispatch('fetchNotes', { params })
  },

  async fetchVenueCityGuide ({ dispatch }, { id, more }) {
    const params = { ...SEARCHES.CITYGUIDE }
    params.filter.venue_id = id
    dispatch('fetchNotes', { params, more })
  },

  async clearSubscribedNotes ({ commit, rootGetters }, { prevCats }) {
    const categories = rootGetters['user/account/favoriteContentCategories']
    if (categories.length === 0) {
      const params = Object.assign({}, SEARCHES.SUBSCRIBED)
      params.filter.contentCategoryId = prevCats.map(cat => cat.id).toString()
      commit('clearSubscribedNotes', { params })
    }
  }
}
